import React from 'react';
import { bcGovernmentServices, getServicesByCategory } from '../data/bcGovernmentServices';

const OtherBCServices: React.FC = () => {
  const [expandedCategory, setExpandedCategory] = React.useState<string | null>(null);
  const servicesByCategory = getServicesByCategory();

  const categoryIcons: Record<string, string> = {
    'Identity & Documents': '🆔',
    'Family & Children': '👨‍👩‍👧‍👦',
    'Financial & Tax': '💰',
    'Housing & Transportation': '🏠',
    'Employment & Education': '💼',
    'Health & Wellness': '🏥',
    'Legal & Social': '⚖️',
    'Emergency & Essential': '🚨'
  };

  return (
    <div className="mt-10 p-6 bg-slate-50 rounded-xl border-2 border-slate-200">

      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-3">📋 Other BC Government Services</h2>
        <p className="text-slate-500 max-w-xl mx-auto">
          Even if you don't need these now, you might need them later.
          Save this page or bookmark these links!
        </p>
      </div>

      {/* Categories */}
      <div className="flex flex-col gap-4 mb-8">
        {Array.from(servicesByCategory.entries()).map(([category, services]) => (
          <div key={category} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div
              className="flex justify-between items-center px-4 py-4 bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
              onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
            >
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                <span className="text-xl">{categoryIcons[category] || '📌'}</span>
                <span>{category}</span>
                <span className="text-sm text-slate-500 font-normal">({services.length})</span>
              </div>
              <span className="text-slate-500">{expandedCategory === category ? '▼' : '▶'}</span>
            </div>

            {expandedCategory === category && (
              <div className="p-4 flex flex-col gap-4">
                {services.map((service) => (
                  <div key={service.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-base font-semibold text-slate-800">{service.name}</h4>
                      {service.priority === 'high' && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded">
                          Important
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2 leading-relaxed">{service.description}</p>
                    <p className="text-xs text-slate-500 mb-3">
                      <strong>Who needs it:</strong> {service.whoNeedsIt}
                    </p>
                    <div className="flex gap-3 flex-wrap">
                      <a href={service.link} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-medium no-underline hover:underline">
                        🔗 Visit Website
                      </a>
                      {service.phone && (
                        <a href={`tel:${service.phone}`}
                          className="text-emerald-600 text-sm font-medium no-underline hover:underline">
                          📞 {service.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div className="mt-8 p-6 bg-blue-50 rounded-lg">
        <h3 className="text-xl font-bold text-slate-800 mb-4">🔗 Quick Access Links</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { href: 'https://www2.gov.bc.ca/gov/content/governments/organizational-structure/ministries-organizations/ministries', label: '🏛️ All BC Government Services' },
            { href: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/new-immigrants.html', label: '🇨🇦 Federal Services for Newcomers' },
            { href: 'https://bc.211.ca', label: '📞 BC 211 - Find Any Service' },
            { href: 'https://www.welcomebc.ca', label: '👋 WelcomeBC - Newcomer Info' },
          ].map(({ href, label }) => (
            <a key={href} href={href} target="_blank" rel="noopener noreferrer"
              className="block px-4 py-3 bg-white border border-blue-200 rounded-lg text-blue-800 text-sm font-medium no-underline hover:bg-blue-100 transition-colors">
              {label}
            </a>
          ))}
        </div>
      </div>

      {/* Emergency Numbers */}
      <div className="mt-6 p-6 bg-red-50 rounded-lg border-2 border-red-200">
        <h3 className="text-xl font-bold text-red-800 mb-4">🚨 Emergency & Important Numbers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Emergency (Police, Fire, Ambulance)', number: '911' },
            { label: 'Crisis Line (24/7)', number: '1-800-784-2433' },
            { label: 'BC 211 (Find Services)', number: '211' },
            { label: 'HealthLink BC (Health Info)', number: '811' },
          ].map(({ label, number }) => (
            <div key={number} className="p-3 bg-white rounded-lg text-sm text-slate-800">
              <strong>{label}:</strong> {number}
            </div>
          ))}
        </div>
      </div>

      {/* Tip */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
        <p className="text-sm text-slate-700">
          💡 <strong>Tip:</strong> Most government services are free or low-cost.
          Never pay someone to help you apply for government services - official applications are always free!
        </p>
      </div>
    </div>
  );
};

export default OtherBCServices;
